import { Component, OnInit } from '@angular/core'
import { collection, getDocs, query, where } from '@angular/fire/firestore'
import { Administrator, EnterpriseManager, FirestoreStructure, SalesManager } from '@parkupp/core'
import { AdminService } from 'src/app/services/admin.service'

interface AuditUser {
    uid: string
    name: string
    email: string
    active: boolean
    role: string
    enterpriseKey?: string
}

@Component({
    selector: 'app-admin-access-audit',
    templateUrl: './admin-access-audit.component.html',
    styleUrls: ['./admin-access-audit.component.scss'],
})
export class AdminAccessAuditComponent implements OnInit {
    auditUsers: AuditUser[] = []
    loading = true
    generatedAt = new Date()

    constructor(private adminService: AdminService) {}

    async ngOnInit() {
        await this.loadAuditData()
    }

    async loadAuditData() {
        this.loading = true
        this.generatedAt = new Date()

        const [administrators, salesManagers, enterpriseManagers] = await Promise.all([
            this.loadCollection<Administrator>(FirestoreStructure.ADMINISTRATORS, 'Administrator'),
            this.loadCollection<SalesManager>(FirestoreStructure.SALES_MANAGERS, 'Sales Manager'),
            this.loadCollection<EnterpriseManager>(FirestoreStructure.ENTERPRISE_MANAGERS, 'Enterprise Manager'),
        ])

        const allUsers = [...administrators, ...salesManagers, ...enterpriseManagers]

        const uids = allUsers.map((u) => u.uid)
        const userInfoMap = await this.lookupUsersByUids(uids)

        this.auditUsers = allUsers.map((u) => {
            const info = userInfoMap[u.uid]
            return {
                ...u,
                name: info?.displayName || u.uid,
                email: info?.email || '',
            }
        })

        this.auditUsers.sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name))
        this.loading = false
    }

    private async loadCollection<T>(collectionName: string, roleName: string): Promise<AuditUser[]> {
        const snapshot = await getDocs(collection(this.adminService.firestore, collectionName))
        return snapshot.docs.map((doc) => {
            const data = doc.data()
            return {
                uid: doc.id,
                name: '',
                email: '',
                active: data['active'] !== false,
                role: roleName,
                enterpriseKey: data['enterpriseKey'],
            }
        })
    }

    private async lookupUsersByUids(uids: string[]): Promise<{ [uid: string]: { email: string; displayName: string } }> {
        const result: { [uid: string]: { email: string; displayName: string } } = {}

        for (const uid of uids) {
            try {
                const snapshot = await getDocs(query(collection(this.adminService.firestore, FirestoreStructure.ADMIN_CLIENTS), where('__name__', '==', uid)))

                if (!snapshot.empty) {
                    const data = snapshot.docs[0].data()
                    result[uid] = {
                        email: data['email'] || '',
                        displayName: data['firstName'] && data['lastName'] ? `${data['firstName']} ${data['lastName']}` : data['email'] || uid,
                    }
                }
            } catch (error) {
                console.error(`Error looking up user ${uid}:`, error)
            }
        }

        return result
    }
}
